import {FALLBACK_LANGUAGE, i18n} from '@online-offer/i18n';
import {ErrorBoundary} from '@sentry/react';

import {useEffect, useLayoutEffect} from 'react';
import {Outlet, useParams} from 'react-router-dom';

import {any, isNotNil, keys} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useOnMount} from 'shared/web';

import {ErrorSection} from '../../(sections)/ErrorSection/ErrorSection';
import {Loading} from '../../(sections)/Loading/Loading';
import {NotFoundPage} from '../../(sections)/NotFoundPage/NotFoundPage';
import {useGetAllApiEnumsQuery, useGetVehicleAuditQuery} from '../../api/digitalCertificateApi';
import {useGetBusinessCaseOnlineOfferQuery, useListLogosQuery} from '../../api/onlineOfferApi';
import {languageDialogItems} from '../../consts/languageDialogItems';
import {queryParams} from '../../consts/queryParams';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {sentryInit} from '../../utils/sentryInit';
import {useQueryState} from '../../utils/useQueryState';

const {workspace} = getWorkspaceFromUri();

export function ApplicationInitializeBoundary() {
  const params = useParams<{offerId: string}>();
  const [language] = useQueryState(queryParams.LNG);
  const [openedBy] = useQueryState(queryParams.OPENED_BY);

  const {isFetching: isListLogosQueryFetching} = useListLogosQuery(
    {'X-Workspace': workspace ?? ''},
    {skip: isNilOrEmpty(workspace)}
  );

  const {
    data: onlineOfferData,
    isError: isOnlineOfferQueryErrored,
    isFetching: isOnlineOfferQueryFetching,
    isLoading: isOnlineOfferQueryLoading,
    error: onlineOfferQueryError,
  } = useGetBusinessCaseOnlineOfferQuery({
    offerId: params.offerId ?? '',
    workspace: workspace ?? '',
    openedBy: openedBy ?? null,
  });

  const {
    data: alphaCatalogue,
    isError: isAlphaCatalogueErrored,
    isFetching: isAlphaCatalogueFetching,
  } = useGetAllApiEnumsQuery(
    {
      lang: [language ?? i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
      vehicleType: onlineOfferData?.vehicle?.type ? [onlineOfferData.vehicle.type] : undefined,
    },
    {skip: isOnlineOfferQueryLoading}
  );

  // Fetch digital certificate only if certificate(carAuditId) is present
  const {
    data: vehicleAudit,
    isError: isVehicleAuditErrored,
    isFetching: isVehicleAuditFetching,
  } = useGetVehicleAuditQuery(
    {auditId: onlineOfferData?.carAuditId ?? '', workspace: workspace ?? ''},
    {skip: isNilOrEmpty(onlineOfferData?.carAuditId)}
  );

  const isFetching = any(isTrue, [
    isOnlineOfferQueryFetching,
    isAlphaCatalogueFetching,
    isVehicleAuditFetching,
    isListLogosQueryFetching,
  ]);

  const isErrored = any(isTrue, [
    isOnlineOfferQueryErrored,
    isAlphaCatalogueErrored,
    isVehicleAuditErrored,
  ]);

  const is404Error =
    onlineOfferQueryError &&
    'status' in onlineOfferQueryError &&
    onlineOfferQueryError.status === 404;

  const isAppReady =
    isNotNil(onlineOfferData) &&
    isNotNil(alphaCatalogue) &&
    // Require loading of certificate only if certificate(carAuditId) is present
    (isNilOrEmpty(onlineOfferData?.carAuditId) || isNotNil(vehicleAudit));

  useLayoutEffect(() => {
    if (isSupportedLanguage(language) && language !== i18n.resolvedLanguage) {
      i18n.changeLanguage(language!);
    }
  }, [language]);

  useOnMount(() => {
    sentryInit();
  });

  useEffect(() => {
    if (!isAppReady || process.env.ENV_TYPE !== 'prod') {
      return;
    }

    const dataLayer = {
      event: 'applicationInitializeBoundary',
      workspace,
      environment: process.env.ENV_TYPE,
      offerId: params.offerId,
    };

    window.dataLayer?.push(dataLayer);
  }, [isAppReady, language, params.offerId, vehicleAudit]);

  const getComponent = () => {
    if (is404Error) {
      return <NotFoundPage />;
    }
    if (isErrored) {
      return <ErrorSection />;
    }
    if (isFetching || isFalse(isAppReady)) {
      return <Loading />;
    }
    return <Outlet />;
  };

  return <ErrorBoundary fallback={<ErrorSection />}>{getComponent()}</ErrorBoundary>;
}

const isSupportedLanguage = (lng: any) =>
  isNotNilOrEmpty(lng) && keys(languageDialogItems).includes(lng);
